import { motion } from 'framer-motion';
import {
  ArrowRight,
  Car,
  Clock,
  CreditCard,
  Headphones,
  MapPin,
  ShieldCheck,
  Star,
  Users,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';

const fadeUp = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } };

const howItWorks = [
  {
    step: '01',
    title: 'Choose your service',
    body: 'Pick driver hire or cab hire, then choose the right need: city driving, airport transfer, office pickup and drop, outstation, night driver, permanent duty, corporate travel, or holiday planning.',
  },
  {
    step: '02',
    title: 'Share trip details',
    body: 'Add pickup and drop locations, schedule, estimated duration or distance, and any instructions your driver or cab partner should know before dispatch.',
  },
  {
    step: '03',
    title: 'Estimate and book',
    body: 'Review the fare estimate, confirm the booking, and track recent requests from the Drivor product area with payment and support workflows ready when needed.',
  },
];

const features = [
  {
    icon: Users,
    title: 'Experienced drivers',
    body: 'Drivers have 3+ years of experience and complete background verification before serving customers.',
  },
  {
    icon: Clock,
    title: '24/7 availability',
    body: 'Book drivers and cabs around the clock across online, app, IVR, and phone-supported channels.',
  },
  {
    icon: ShieldCheck,
    title: 'Safety first',
    body: 'Verified service partners, clean records, trip visibility, and support workflows keep each booking accountable.',
  },
  {
    icon: Star,
    title: 'Quality service',
    body: 'Professional, courteous, and reliable service for personal travel, office routines, events, and corporate needs.',
  },
  {
    icon: MapPin,
    title: 'Wide coverage',
    body: 'Service coverage spans Bengaluru, Chennai, airport routes, city rides, and nearby outstation travel.',
  },
  {
    icon: Headphones,
    title: 'Customer support',
    body: 'Dedicated support helps with online bookings, phone requests, changes, and service coordination.',
  },
];

const whyDrivor = [
  {
    icon: ShieldCheck,
    title: 'Verified people, accountable rides',
    body: 'Drivers and service partners are background checked, experienced, and supported by trip visibility from booking through completion.',
  },
  {
    icon: Car,
    title: 'Driver hire and cab hire in one place',
    body: 'Use one Drivor workflow for city drivers, night drivers, office pickup and drop, airport transfers, local trips, and outstation travel.',
  },
  {
    icon: Clock,
    title: 'Fast response when plans change',
    body: 'Quick dispatch and 24/7 booking channels help you arrange late-night drops, urgent city rides, recurring commutes, and planned travel.',
  },
  {
    icon: CreditCard,
    title: 'Estimates and payment-ready workflows',
    body: 'Review fare estimates before confirming, then use booking and payment workflows built for card, UPI, wallet, and cash operations.',
  },
];

const serviceHighlights = [
  'City driving',
  'Airport transfer',
  'Office pickup and drop',
  'Night driver',
  'Outstation trip',
  'Corporate driver',
  'Holiday planning',
  'Permanent duty',
];

const Home = () => (
  <>
    <SEOHead
      absoluteTitle="Drivor | Driver and cab hire for city, airport, office, and outstation travel"
      description="Book verified drivers and well-maintained cabs through Drivor for city drives, airport transfers, office pickup and drop, outstation trips, and holiday travel."
    />

    {/* Hero */}
    <section className="bg-hero-fade py-24 lg:py-32">
      <div className="container-custom text-center">
        <motion.div {...fadeUp} transition={{ duration: 0.4 }}>
          <p className="eyebrow">Driver and cab hire</p>
          <h1 className="section-title mx-auto mt-3 max-w-3xl">
            Professional drivers and cabs for everyday travel
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-surface-body">
            Drivor helps you hire verified drivers or book reliable cabs for city
            travel, airport transfers, office commutes, outstation trips, events,
            and corporate needs across Bengaluru, Chennai, and nearby routes.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <a
              href="https://app.drivor.in/signup"
              rel="noopener noreferrer"
              className="btn-primary"
            >
              Start booking
            </a>
            <a href="#how" className="btn-secondary">
              See services
              <ArrowRight size={16} className="ml-2" aria-hidden />
            </a>
          </div>
        </motion.div>
      </div>
    </section>

    {/* How it works */}
    <section id="how" className="bg-white py-20">
      <div className="container-custom">
        <div className="mx-auto max-w-2xl text-center">
          <p className="eyebrow">How it works</p>
          <h2 className="section-title mt-2">Book the ride or driver you need in minutes</h2>
        </div>
        <div className="mx-auto mt-14 grid max-w-4xl gap-8 md:grid-cols-3">
          {howItWorks.map(({ step, title, body }) => (
            <motion.div
              key={step}
              {...fadeUp}
              className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card"
            >
              <span className="text-3xl font-bold text-primary-200">{step}</span>
              <h3 className="mt-3 text-lg font-semibold text-surface-ink">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-surface-body">{body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* Key features */}
    <section className="bg-surface-page py-20">
      <div className="container-custom">
        <div className="mx-auto max-w-2xl text-center">
          <p className="eyebrow">Features</p>
          <h2 className="section-title mt-2">Built around safe, reliable mobility</h2>
        </div>
        <div className="mx-auto mt-14 grid max-w-5xl gap-6 md:grid-cols-3">
          {features.map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className="rounded-2xl border border-slate-200 bg-surface-card p-6 shadow-card"
            >
              <Icon size={28} className="text-primary-600" aria-hidden />
              <h3 className="mt-4 text-base font-semibold text-surface-ink">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-surface-body">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Why Drivor */}
    <section className="bg-white py-20">
      <div className="container-custom">
        <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <div>
            <p className="eyebrow">Why Drivor</p>
            <h2 className="section-title mt-2">A safer way to arrange drivers and cabs</h2>
            <p className="mt-5 text-lg leading-relaxed text-surface-body">
              Drivor combines verified drivers, maintained cabs, quick dispatch,
              live booking visibility, fare estimates, and support into one
              practical travel workflow for individuals and teams.
            </p>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {[
                { value: 'Experienced', label: 'drivers' },
                { value: '24/7', label: 'booking availability' },
              ].map(({ value, label }) => (
                <div key={label} className="border-l-2 border-primary-500 pl-4">
                  <p className="text-2xl font-bold text-surface-ink">{value}</p>
                  <p className="mt-1 text-sm text-surface-body">{label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            {whyDrivor.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="rounded-2xl border border-slate-200 bg-slate-50 p-6"
              >
                <Icon size={26} className="text-primary-600" aria-hidden />
                <h3 className="mt-4 text-base font-semibold text-surface-ink">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-surface-body">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* Services */}
    <section className="bg-surface-page py-16">
      <div className="container-custom">
        <p className="text-center text-sm font-semibold uppercase tracking-wider text-slate-500">
          Popular Drivor services
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          {serviceHighlights.map(label => (
            <div
              key={label}
              className="flex min-h-[2.25rem] items-center justify-center rounded-full border border-slate-200 bg-slate-50 px-4 text-sm font-medium text-slate-700"
            >
              {label}
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Bottom CTA */}
    <section className="bg-primary-600 py-20">
      <div className="container-custom text-center">
        <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
          Ready to book safer travel?
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-lg text-primary-100">
          Start with a driver, cab, airport transfer, office commute, or
          outstation request and let Drivor coordinate the service workflow.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <a
            href="https://app.drivor.in/signup"
            rel="noopener noreferrer"
            className="rounded-lg bg-white px-6 py-3 text-sm font-semibold text-primary-700 shadow-soft transition hover:bg-primary-50"
          >
            Start booking
          </a>
          <a
            href="/demo"
            className="rounded-lg border border-primary-400 px-6 py-3 text-sm font-semibold text-white transition hover:bg-primary-700"
          >
            Try demo
          </a>
        </div>
      </div>
    </section>
  </>
);

export default Home;
